import { Box, CardContent, FormControlLabel, Link, TableBody, TableCell, TableRow, Tooltip } from '@mui/material';
import {
  PermissionEnum,
  useConfig,
  useNotification,
  useOidcWithAdmin,
  usePermission
} from '@revenuewell/front-end-bundle';
import { SocialMediaService } from 'apps/wrapper/src/services/social-media/social-media-service';
import { SocialMediaSettings } from 'apps/wrapper/src/services/social-media/types/social-media-settings';
import moment from 'moment-timezone';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { usePatientProfile } from '../../../../../hooks/use-patient-profile';
import { ContactApi } from '../../../../../services/api-client/contact-api';
import PatientFeedbackService from '../../../../../services/patient-feedback-service/patient-feedback-service';
import { Review } from '../../../../../services/patient-feedback-service/patient-feedback-service-types';
import { PrimaryButton } from '../../../buttons';
import { Card } from '../../../card/card.style';
import { CloseDialogButton, Dialog, DialogContent, DialogContentWrapper } from '../../../dialog/dialog.styles';
import { FeatureNotAllowedModal } from '../../../feature/feature-not-allowed/feature-not-allowed';
import { StarRating } from '../../../star-rating/star-rating';
import { OrangeGreenSwitch } from '../../../switch/switch.styles';
import { CardTitle, DialogTitle, Hr, InfoTitle, Table, TextField } from '../dialog.styles';
import { NotFeaturedReviewDialog } from './not-featured-review-dialog';
import { PostToButtons } from './post-to-buttons/post-to-buttons';
import { ReviewSummarySkeleton } from './review-summary.skeleton';

interface ReviewSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  reviewId: number | null;
}

export const ReviewSummaryDialog: FC<ReviewSummaryDialogProps> = (props) => {
  const { open, reviewId, onClose } = props;
  const { config } = useConfig();
  const { oidcService, getClaims } = useOidcWithAdmin();
  const { showError } = useNotification();
  const { hasPermission } = usePermission();
  const { contactDetails, locationId } = usePatientProfile();
  const [isLoading, setIsLoading] = useState(true);
  const [isPosting, setIsPosting] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isEditMode, setIsEditMode] = useState(true);
  const [reviewText, setReviewText] = useState('');
  const [review, setReview] = useState<Review | null>(null);
  const [socialMediaSettings, setSocialMediaSettings] = useState<SocialMediaSettings | null>(null);
  const [isReviewFeatured, setIsReviewFeatured] = useState(false);
  const [notFeaturedReviewDialogOpened, setNotFeaturedReviewDialogOpened] = useState(false);

  const hasFeatureReviewPermission = useMemo(() => hasPermission(PermissionEnum.FeatureReviewOrRespond), []);
  const patientFeedbackService = useMemo(
    () => PatientFeedbackService.getInstance(config, oidcService),
    [config, oidcService]
  );
  const socialMediaService = useMemo(() => new SocialMediaService(config, oidcService), [config, oidcService]);
  const contactApi = useMemo(() => ContactApi.getInstance(), [ContactApi]);

  if (open && !hasPermission(PermissionEnum.ViewReview)) return <FeatureNotAllowedModal onClose={onClose} />;

  const initiate = useCallback(
    async (reviewId: number, locId: number) => {
      try {
        setIsLoading(true);

        const reviewResponse = await patientFeedbackService.getReview(locId, reviewId);
        const settings = await socialMediaService.getSocialMediaSettings(locId);

        setReview(reviewResponse);
        setReviewText(reviewResponse.reply || '');
        setSocialMediaSettings(settings);
        setIsEditMode(!reviewResponse.reply);
        setIsReviewFeatured(reviewResponse.featured);
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        showError('Failed to load review details');
        setIsLoading(false);
        onClose();
      }
    },
    [config, contactApi]
  );

  useEffect(() => {
    if (!open || !reviewId || !hasPermission(PermissionEnum.ViewReview)) {
      if (!open || !reviewId) onClose();
      return;
    }

    if (locationId) {
      setReview(null);
      initiate(reviewId, locationId);
    } else {
      // No locationId from patient profile (e.g. anonymous review) — get from claims
      getClaims().then((claims) => {
        const claimLocationId = Number(claims.locationId);
        if (claimLocationId) {
          setReview(null);
          initiate(reviewId, claimLocationId);
        } else {
          onClose();
        }
      });
    }
  }, [open, reviewId, locationId]);

  const setReviewAsFeatured = async (isFeatured: boolean) => {
    if (!review) return;

    try {
      setIsDisabled(true);
      await patientFeedbackService.setReviewAsFeatured(review.practiceId, review.id, isFeatured);
      setIsReviewFeatured(isFeatured);
    } catch (error) {
      showError('Failed to set review as featured');
    } finally {
      setIsDisabled(false);
    }
  };

  const handlePostReplyClick = async () => {
    if (!isReviewFeatured) {
      setNotFeaturedReviewDialogOpened(true);

      return;
    }

    await postReply();
    setIsEditMode(false);
  };

  const postReply = async () => {
    if (!review || !contactDetails) return;

    try {
      setIsPosting(true);
      await patientFeedbackService.postReply(review.practiceId, contactDetails.id, review.id, reviewText);
    } catch (error) {
      showError('Failed to post reply');
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <>
      <Dialog open={open} maxWidth={false}>
        <DialogContentWrapper>
          <DialogTitle sx={{ cursor: 'default' }}>
            {contactDetails ? `${contactDetails.firstName} ${contactDetails.lastName}` : 'Review'}
            <CloseDialogButton onClick={onClose} />
          </DialogTitle>
          <DialogContent>
            {isLoading && <ReviewSummarySkeleton />}
            {!isLoading && (
              <Card>
                <CardContent style={{ paddingBottom: 0 }}>
                  <CardTitle>
                    Review Summary
                    <span>{moment(review?.date).format('MM/DD/YYYY hh:mm A')}</span>
                  </CardTitle>
                  <Table className="table-info" style={{ marginBottom: 0 }}>
                    <TableBody>
                      <TableRow>
                        <TableCell style={{ width: '15%' }}>
                          <InfoTitle>Rating:</InfoTitle>
                        </TableCell>
                        <TableCell>
                          <StarRating rating={review?.rating || 0} />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ width: '15%' }}>
                          <InfoTitle>Title:</InfoTitle>
                        </TableCell>
                        <TableCell>{review?.title || '-'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ width: '15%' }}>
                          <InfoTitle>Review:</InfoTitle>
                        </TableCell>
                        <TableCell>{review?.text || '-'}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <PostToButtons
                    review={review}
                    socialMediaSettings={socialMediaSettings}
                    onPostSend={async () => onClose()}
                  />
                  <Hr />
                  {hasFeatureReviewPermission && (
                    <>
                      <Table className="table-info" style={{ marginBottom: 0, width: '100%' }}>
                        <TableBody>
                          <TableRow>
                            <TableCell style={{ width: '100%' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <InfoTitle>Feature Review</InfoTitle>
                                <FormControlLabel
                                  label={
                                    <Box sx={{ fontSize: '13px', color: '#1C3D64' }}>
                                      {isReviewFeatured ? 'Yes' : 'No'}
                                    </Box>
                                  }
                                  control={
                                    <OrangeGreenSwitch
                                      color="default"
                                      checked={isReviewFeatured}
                                      disabled={isDisabled}
                                      onChange={() => setReviewAsFeatured(!isReviewFeatured)}
                                    />
                                  }
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell style={{ width: '100%', padding: '0 16px 16px 16px', fontSize: '13px' }}>
                              Featuring a review will stream it to your{' '}
                              <Link
                                href={`${config.patient.websiteUrl}/${review?.practiceMicrositeUrl}`}
                                target="_blank"
                                style={{ textDecoration: 'none' }}
                              >
                                PatientConnect365 profile page
                              </Link>
                              , as well as to your website and Facebook Page (if you have the website and Facebook
                              widgets installed).
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                      <Hr />
                    </>
                  )}

                  <Table className="table-info" style={{ marginBottom: 0 }}>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <InfoTitle>{isEditMode ? 'Reply Publicly:' : 'Your Public Reply to this Review:'}</InfoTitle>
                          {isEditMode && (
                            <TextField
                              disabled={!hasFeatureReviewPermission}
                              value={reviewText || ''}
                              onChange={(e) => setReviewText(e.target.value)}
                              multiline
                              fullWidth
                              rows={7}
                            />
                          )}
                          {!isEditMode && <Box sx={{ marginTop: '16px' }}>{reviewText}</Box>}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <Box sx={{ textAlign: 'right', padding: '0 10px 16px' }}>
                    {isEditMode && (
                      <Tooltip title={hasFeatureReviewPermission ? null : "You don't have permission to post a reply"}>
                        <PrimaryButton
                          loading={isPosting}
                          disabled={!hasFeatureReviewPermission || isDisabled}
                          onClick={handlePostReplyClick}
                        >
                          Post Reply
                        </PrimaryButton>
                      </Tooltip>
                    )}
                    {!isEditMode && (
                      <Tooltip
                        title={hasFeatureReviewPermission ? null : "You don't have permission to edit the reply"}
                      >
                        <PrimaryButton
                          loading={false}
                          disabled={!hasFeatureReviewPermission || isDisabled}
                          onClick={() => setIsEditMode(true)}
                        >
                          Edit Reply
                        </PrimaryButton>
                      </Tooltip>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
          </DialogContent>
        </DialogContentWrapper>
      </Dialog>

      <NotFeaturedReviewDialog
        opened={notFeaturedReviewDialogOpened}
        onClose={() => setNotFeaturedReviewDialogOpened(false)}
      />
    </>
  );
};
